import React from "react"
import {useTranslation} from "react-i18next"
import {useInView} from "react-intersection-observer"

import {GenericErrorAlert} from "../../../../components/Alert.tsx"
import {Table} from "../../../../components/Table/Table.tsx"
import {TableNoRows} from "../../../../components/Table/TableNoRows.tsx"
import {TableRow} from "../../../../components/Table/TableRow.tsx"
import {getColumnsFromMeta} from "../../../../components/Table/utils/columns.ts"
import {TColumnsMetaWithEmpty} from "../../../../components/Table/utils/shared.ts"
import {useLSTableColumnsState} from "../../../../components/Table/utils/useLSTableColumnsState.tsx"
import {useInfiniteSalesAssistantSalesPeopleQuery} from "../../../../queries/salesAssistant.ts"
import {ASalesAssistantSalesPerson} from "../../../../services/types.generated.ts"
import {EMPTY_ARRAY} from "../../../../utils/index.ts"
import {LeaderboardPodium} from "../../components/LeaderboardPodium.tsx"
import {TSalesPeopleTableColumn} from "../../utils.ts"

const tableId = "sales assistant leaderboard"
const PAGE_SIZE = 10

export const LeaderboardSection: React.FC = () => {
  const {t} = useTranslation()

  const {data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch} =
    useInfiniteSalesAssistantSalesPeopleQuery({
      pageSize: PAGE_SIZE,
    })

  const allSalesPeople = React.useMemo(() => {
    if (!data?.pages) {
      return EMPTY_ARRAY
    }
    return data.pages.flatMap(page => (page.data?.sales_people ?? EMPTY_ARRAY) as ASalesAssistantSalesPerson[])
  }, [data?.pages])

  const topThree = allSalesPeople.slice(0, 3)
  const remainingSalesPeople = allSalesPeople.slice(3)

  const [scrollableContainer, setScrollableContainer] = React.useState<HTMLDivElement | null>(null)
  const scrollableContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    setScrollableContainer(node)
  }, [])

  const {ref: loadMoreRef} = useInView({
    root: scrollableContainer,
    rootMargin: "100px",
    skip: !hasNextPage || isFetchingNextPage || !scrollableContainer,
    onChange: inView => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  const columnsMeta = React.useMemo<TColumnsMetaWithEmpty<TSalesPeopleTableColumn, ASalesAssistantSalesPerson>>(
    () =>
      [
        {
          column: "order",
          HeaderCellValue: () => "#",
          CellValue: ({rowIndex}) => (
            <span className={"ext-gray-900 font-['Sora'] text-xs font-normal"}>{(rowIndex ?? 0) + 4}</span>
          ),
        },
        {
          column: "name",
          HeaderCellValue: () => t("SalesAssistant_Leaderboard_Table_SalesReps"),
          CellValue: ({row}) => (
            <div className={"flex items-center gap-3"}>
              <span className={"font-medium"}>
                {row.first_name} {row.last_name}
              </span>
            </div>
          ),
        },
        {
          column: "sales_assistant_updates",
          HeaderCellValue: () => t("SalesAssistant_Leaderboard_Table_KlaraUpdates"),
          CellValue: ({row}) => (
            <span className={"font-['Sora'] text-xs font-bold text-gray-900"}>{row.sales_assistant_updates}</span>
          ),
        },
        {
          column: "total_updates",
          HeaderCellValue: () => t("SalesAssistant_Leaderboard_Table_TotalUpdates"),
          CellValue: ({row}) => (
            <span className={"font-['Sora'] text-xs font-normal text-gray-900"}>{row.total_updates}</span>
          ),
        },
      ] as const,
    [t]
  )

  const columnsState = useLSTableColumnsState(tableId, {
    columnsOrder: getColumnsFromMeta(columnsMeta),
    pinnedColumn: null,
  })

  if (error) {
    return (
      <LeaderboardCard>
        <GenericErrorAlert retry={refetch} />
      </LeaderboardCard>
    )
  }

  return (
    <LeaderboardCard>
      <div className={"flex items-center justify-between"}>
        <h2 className={"text-2xl font-bold"}>{t("SalesAssistant_Leaderboard_Title")}</h2>
      </div>

      <LeaderboardPodium topThree={topThree} />

      <div className={"flex grow flex-col"}>
        <div ref={scrollableContainerRef} className={"scrollbar-slim max-h-[300px] min-h-[300px] overflow-auto"}>
          {remainingSalesPeople.length > 0 ? (
            <>
              <Table<TSalesPeopleTableColumn, ASalesAssistantSalesPerson>
                {...columnsState}
                data={remainingSalesPeople}
                loading={isFetching}
                ghost
                columnsMeta={columnsMeta}
              >
                {({data, pinnedColumn}) => (
                  <>
                    {data.length > 0 &&
                      data.map((row, index) => (
                        <TableRow
                          key={row.id}
                          testId={`row-${row.id}`}
                          row={row}
                          rowIndex={index}
                          className={"whitespace-pre-wrap"}
                          pinnedColumn={pinnedColumn}
                        />
                      ))}
                  </>
                )}
              </Table>
              {hasNextPage && <div ref={loadMoreRef} aria-hidden={"true"} />}
            </>
          ) : (
            allSalesPeople.length === 0 &&
            !isFetching && <TableNoRows>{t("SalesAssistant_Leaderboard_Table_NoSalesPeople")}</TableNoRows>
          )}
        </div>
      </div>
    </LeaderboardCard>
  )
}

const LeaderboardCard: React.FC<{children: React.ReactNode}> = ({children}) => {
  return <div className={"flex flex-col gap-6 card-shadow p-6"}>{children}</div>
}
